/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Agent Sub-Agent Manager Component
 * UI for assigning and managing sub-agents (agents as tools) for an agent
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  SmartToy as AgentIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentApi } from '../../services/api';
import { formatStoredDate, formatStoredDateTime } from '../../utils/datetime';
import { LoadingSpinner } from '../common';
import type { 
  SubAgentAssignment, 
  SubAgentAvailable, 
  SubAgentAssignmentRequest,
  SubAgentAssignmentUpdate,
} from '../../types';

interface AgentSubAgentManagerProps {
  agentId: number;
  agentName?: string;
}

export const AgentSubAgentManager: React.FC<AgentSubAgentManagerProps> = ({ 
  agentId,
  agentName = 'Agent',
}) => {
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSubAgent, setSelectedSubAgent] = useState<SubAgentAvailable | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<SubAgentAssignment | null>(null);
  
  // Form state
  const [toolName, setToolName] = useState('');
  const [toolDescription, setToolDescription] = useState('');
  const [customConfig, setCustomConfig] = useState<string>('{}');
  const [isEnabled, setIsEnabled] = useState(true);

  // Fetch sub-agents data
  const { data: subAgentsData, isLoading, error } = useQuery({
    queryKey: ['agent-sub-agents', agentId],
    queryFn: () => agentApi.getSubAgents(agentId),
    enabled: !!agentId,
  });

  // Assign sub-agent mutation
  const assignMutation = useMutation({
    mutationFn: (data: SubAgentAssignmentRequest) => agentApi.assignSubAgent(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-sub-agents', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent-tools', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      toast.success('Sub-agent assigned successfully');
      handleCloseAssignDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to assign sub-agent');
    },
  });

  // Update assignment mutation
  const updateMutation = useMutation({
    mutationFn: ({ assignmentId, data }: { assignmentId: number; data: SubAgentAssignmentUpdate }) => 
      agentApi.updateSubAgentAssignment(agentId, assignmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-sub-agents', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent-tools', agentId] });
      toast.success('Sub-agent configuration updated');
      handleCloseConfigDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to update configuration');
    },
  });

  // Unassign sub-agent mutation
  const unassignMutation = useMutation({
    mutationFn: (subAgentId: number) => agentApi.unassignSubAgent(agentId, subAgentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-sub-agents', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent-tools', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      toast.success('Sub-agent unassigned successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to unassign sub-agent');
    },
  });

  const handleOpenAssignDialog = (subAgent: SubAgentAvailable) => {
    setSelectedSubAgent(subAgent);
    setToolName('');
    setToolDescription('');
    setCustomConfig(subAgent.id === agentId 
      ? '{"max_iterations": 10, "max_delegation_depth": 3}' 
      : '{"max_iterations": 10}');
    setIsEnabled(true);
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedSubAgent(null);
    setToolName('');
    setToolDescription('');
    setCustomConfig('{}');
    setIsEnabled(true);
  };

  const handleAssignSubAgent = () => {
    if (!selectedSubAgent) return;

    let config = {};
    try {
      config = JSON.parse(customConfig);
    } catch (error) {
      toast.error('Invalid JSON configuration');
      return;
    }

    assignMutation.mutate({
      sub_agent: selectedSubAgent.id,
      tool_name: toolName || undefined,
      tool_description: toolDescription || undefined,
      custom_config: config,
      is_enabled: isEnabled,
    });
  };

  const handleOpenConfigDialog = (assignment: SubAgentAssignment) => {
    setEditingAssignment(assignment);
    setToolName(assignment.tool_name || '');
    setToolDescription(assignment.tool_description || '');
    setCustomConfig(JSON.stringify(assignment.custom_config || {}, null, 2));
    setIsEnabled(assignment.is_enabled);
    setConfigDialogOpen(true);
  };

  const handleCloseConfigDialog = () => {
    setConfigDialogOpen(false);
    setEditingAssignment(null);
    setToolName('');
    setToolDescription('');
    setCustomConfig('{}');
  };

  const handleUpdateConfig = () => {
    if (!editingAssignment) return;

    let config = {};
    try {
      config = JSON.parse(customConfig);
    } catch (error) {
      toast.error('Invalid JSON configuration');
      return;
    }

    updateMutation.mutate({
      assignmentId: editingAssignment.id,
      data: {
        tool_name: toolName || undefined,
        tool_description: toolDescription || undefined,
        custom_config: config,
        is_enabled: isEnabled,
      },
    });
  };

  const handleUnassignSubAgent = (assignment: SubAgentAssignment) => {
    const subAgentName = assignment.sub_agent_details?.name || 'this sub-agent';
    if (window.confirm(`Are you sure you want to remove "${subAgentName}" as a tool?`)) {
      unassignMutation.mutate(assignment.sub_agent);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load sub-agents. Please try again later.
      </Alert>
    );
  }

  const assignedSubAgents = subAgentsData?.assigned || [];
  const availableSubAgents = subAgentsData?.available || [];

  // Filter available sub-agents by search
  const filteredAvailable = availableSubAgents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AgentIcon color="primary" />
                <Box>
                  <Typography variant="h4">{assignedSubAgents.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Assigned Sub-Agents</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlayIcon color="success" />
                <Box>
                  <Typography variant="h4">
                    {assignedSubAgents.filter(a => a.is_enabled).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Enabled</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon color="info" />
                <Box>
                  <Typography variant="h4">{availableSubAgents.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Available to Assign</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Assigned Sub-Agents Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgentIcon /> Assigned Sub-Agents (Agent Tools)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These agents are available as tools for "{agentName}" to delegate tasks.
        </Typography>
        
        {assignedSubAgents.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sub-Agent</TableCell>
                  <TableCell>Tool Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedSubAgents.map((assignment) => (
                  <TableRow key={assignment.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <AgentIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {assignment.sub_agent_details?.name || `Agent #${assignment.sub_agent}`}
                            </Typography>
                            {assignment.sub_agent === agentId && (
                              <Chip label="Self" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {assignment.sub_agent_details?.agent_type || 'ASSISTANT'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {assignment.effective_tool_name}
                      </Typography>
                      {assignment.tool_name && (
                        <Typography variant="caption" color="text.secondary">
                          (custom name)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.is_enabled ? 'Enabled' : 'Disabled'}
                        color={assignment.is_enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{assignment.usage_count}</TableCell>
                    <TableCell>
                      {assignment.last_used
                        ? formatStoredDate(assignment.last_used, undefined, 'Never')
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenConfigDialog(assignment)}
                        >
                          <SettingsIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleUnassignSubAgent(assignment)}
                        >
                          <DeleteIcon />
                        </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No sub-agents assigned yet. Add agents below to enable task delegation.
          </Alert>
        )}
      </Paper>

      {/* Available Sub-Agents Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon /> Available Agents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select agents to add as tools. Sub-agents can help with specialized tasks.
        </Typography>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search agents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, minWidth: 300 }}
        />

        {filteredAvailable.length > 0 ? (
          <Grid container spacing={2}>
            {filteredAvailable.map((agent) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={agent.id}>
                <Card variant="outlined" sx={agent.id === agentId ? { borderColor: 'warning.main', borderWidth: 2 } : {}}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar sx={{ bgcolor: agent.id === agentId ? 'warning.main' : 'secondary.main', width: 40, height: 40 }}>
                        <AgentIcon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="subtitle1" noWrap fontWeight="medium">
                            {agent.name}
                          </Typography>
                          {agent.id === agentId && (
                            <Chip label="Self" size="small" color="warning" variant="outlined" />
                          )}
                        </Box>
                        <Chip
                          label={agent.agent_type}
                          size="small"
                          color={agent.agent_type === 'AI_SYSTEM_BUILDER' ? 'primary' : 'default'}
                        />
                      </Box>
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: 40,
                      }}
                    >
                      {agent.description || 'No description'}
                    </Typography>
                    {agent.model_name && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Model: {agent.model_name}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenAssignDialog(agent)}
                      disabled={assignMutation.isPending}
                      fullWidth
                    >
                      Add as Tool
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            {searchTerm 
              ? 'No agents match your search.'
              : 'No additional agents available. Create more agents to enable delegation.'}
          </Alert>
        )}
      </Paper>

      {/* Assign Sub-Agent Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Sub-Agent: {selectedSubAgent?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedSubAgent && (
              <>
                {selectedSubAgent?.id === agentId ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>Self-delegation:</strong> This agent will be able to delegate tasks to itself.
                    A recursion depth limit (default: 3) is enforced to prevent infinite loops.
                    You can set <code>max_delegation_depth</code> in the config below.
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This agent will be available as a tool that "{agentName}" can use to delegate tasks.
                  </Alert>
                )}

                <Typography variant="body2" paragraph color="text.secondary">
                  {selectedSubAgent.description || 'No description available'}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  label="Custom Tool Name (optional)"
                  value={toolName}
                  onChange={(e) => setToolName(e.target.value)}
                  placeholder={`agent_${selectedSubAgent.name.toLowerCase().replace(/\s+/g, '_')}`}
                  helperText="Leave empty to use auto-generated name"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Custom Description (optional)"
                  value={toolDescription}
                  onChange={(e) => setToolDescription(e.target.value)}
                  multiline
                  rows={2}
                  placeholder={`Delegate task to sub-agent '${selectedSubAgent.name}'`}
                  helperText="Leave empty to use auto-generated description"
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Configuration (JSON):
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={customConfig}
                  onChange={(e) => setCustomConfig(e.target.value)}
                  placeholder='{"max_iterations": 10}'
                  helperText="Optional: Set max_iterations and other config options"
                  sx={{ mb: 2, fontFamily: 'monospace' }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(e) => setIsEnabled(e.target.checked)}
                    />
                  }
                  label="Enable immediately"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignSubAgent}
            disabled={assignMutation.isPending}
          >
            Add Sub-Agent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Config Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={handleCloseConfigDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Configure Sub-Agent: {editingAssignment?.sub_agent_details?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Custom Tool Name"
              value={toolName}
              onChange={(e) => setToolName(e.target.value)}
              helperText="Leave empty to use auto-generated name"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Custom Description"
              value={toolDescription}
              onChange={(e) => setToolDescription(e.target.value)}
              multiline
              rows={2}
              helperText="Leave empty to use auto-generated description"
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Configuration (JSON):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={customConfig}
              onChange={(e) => setCustomConfig(e.target.value)}
              sx={{ mb: 2, fontFamily: 'monospace' }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                />
              }
              label="Enabled"
            />

            {editingAssignment && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Current Tool Name: <code>{editingAssignment.effective_tool_name}</code>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Usage Count: {editingAssignment.usage_count}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last Used: {editingAssignment.last_used 
                    ? formatStoredDateTime(editingAssignment.last_used, undefined, 'Never')
                    : 'Never'}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfigDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateConfig}
            disabled={updateMutation.isPending}
          >
            Update Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentSubAgentManager;
